import {
  webAudioPlayer,
  type SoundName,
  initializeWebAudio,
  resumeWebAudio,
  soundSources,
} from './webAudioPlayer'

// Fallback HTMLAudioElement system for browsers without Web Audio support
const sounds: Record<SoundName, HTMLAudioElement> = {} as Record<SoundName, HTMLAudioElement>
let useWebAudio = true
const soundEntries = Object.entries(soundSources) as Array<[SoundName, string]>

/**
 * Check if user is on a mobile device
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(max-width: 768px)').matches
}

/**
 * Check if Web Audio API is being used
 */
export function isUsingWebAudio(): boolean {
  return useWebAudio && webAudioPlayer.isReady()
}

/**
 * Initialize sound system
 * Tries to use Web Audio API, falls back to HTMLAudioElement if unavailable
 */
export async function initializeSoundFiles(): Promise<void> {
  // Always initialize HTMLAudioElement as fallback (in case Web Audio fails to play)
  soundEntries.forEach(([name, url]) => {
    const audio = new Audio()
    audio.src = url
    audio.volume = 0.15
    audio.preload = 'auto'
    sounds[name] = audio
  })

  // Try to initialize Web Audio
  try {
    await initializeWebAudio()
    useWebAudio = true
  } catch (error) {
    console.warn('Web Audio not available, using HTMLAudioElement fallback')
    useWebAudio = false
  }
}

/**
 * Resume audio (needed on mobile after user interaction)
 */
export async function resumeAudio(): Promise<void> {
  if (useWebAudio) {
    await resumeWebAudio()
  }
}

/**
 * Play a sound immediately
 * Uses Web Audio API if available, falls back to HTMLAudioElement
 * Sounds are disabled on mobile devices
 */
export default function play(name: SoundName): void {
  // Disable sounds on mobile devices
  if (isMobileDevice()) {
    return
  }

  if (useWebAudio && webAudioPlayer.isReady()) {
    const result = webAudioPlayer.play(name)
    if (!result) {
      // Try fallback if Web Audio fails
      const audio = sounds[name]
      if (audio) {
        audio.currentTime = 0
        audio.play().catch((error) => {
          console.warn(`Failed to play fallback sound "${name}":`, error)
        })
      }
    }
  } else {
    // Fallback to HTMLAudioElement
    const audio = sounds[name]
    if (audio) {
      audio.currentTime = 0
      audio.play().catch((error) => {
        console.warn(`Failed to play sound "${name}":`, error)
      })
    }
  }
}

/**
 * Create dedicated sound channel (for concurrent playback)
 * Note: This is kept for backwards compatibility but Web Audio handles this better
 * Sounds are disabled on mobile devices
 */
export function createDedicatedSoundChannel(): (name: SoundName) => void {
  if (useWebAudio && webAudioPlayer.isReady()) {
    // Web Audio naturally supports concurrent playback, just return the play function
    return play
  }

  // Fallback: create dedicated HTMLAudioElement instances
  const dedicatedSounds: Record<SoundName, HTMLAudioElement> = {} as Record<SoundName, HTMLAudioElement>
  soundEntries.forEach(([name, url]) => {
    const audio = new Audio()
    audio.src = url
    audio.volume = 0.15
    dedicatedSounds[name] = audio
  })

  return function playDedicated(name: SoundName): void {
    // Disable sounds on mobile devices
    if (isMobileDevice()) {
      return
    }

    const audio = dedicatedSounds[name]
    if (audio) {
      audio.currentTime = 0
      audio.play().catch((error) => {
        console.warn(`Failed to play sound "${name}":`, error)
      })
    }
  }
}

// Re-export SoundName type for convenience
export type { SoundName }
